import { useState, useCallback } from "react";
import { toast } from "sonner";

/**
 * Hook pour rechercher les informations d'une entreprise via son SIRET
 * Utilise l'API Sirene de l'INSEE via le proxy api.insee.fr
 * 
 * Note: Cette version utilise l'API publique data.gouv.fr (Annuaire des Entreprises)
 * qui ne nécessite pas de clé API
 */
export function useSiretLookup() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const lookupSiret = useCallback(async (siret) => {
    // Nettoyer le SIRET (enlever espaces)
    const cleanSiret = siret.replace(/\s/g, "");
    
    // Vérifier le format
    if (!/^\d{14}$/.test(cleanSiret)) {
      setError("Le SIRET doit contenir 14 chiffres");
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Utiliser l'API Annuaire des Entreprises (data.gouv.fr) - gratuite et sans clé
      const response = await fetch(
        `https://recherche-entreprises.api.gouv.fr/search?q=${cleanSiret}&page=1&per_page=1`
      );

      if (!response.ok) {
        throw new Error("Entreprise non trouvée");
      }

      const data = await response.json();
      
      if (!data.results || data.results.length === 0) {
        throw new Error("Aucune entreprise trouvée avec ce SIRET");
      }

      const entreprise = data.results[0];
      const siege = entreprise.siege;

      // Extraire les informations
      const result = {
        company_name: entreprise.nom_complet || entreprise.nom_raison_sociale,
        siret: cleanSiret,
        siren: entreprise.siren,
        address: siege?.adresse ? formatAddress(siege) : "",
        postal_code: siege?.code_postal || "",
        city: siege?.libelle_commune || "",
        department: siege?.code_postal?.substring(0, 2) || "",
        // Informations supplémentaires
        naf_code: entreprise.activite_principale || "",
        naf_label: entreprise.activite_principale_libelle || "",
        legal_form: entreprise.nature_juridique_libelle || "",
        employee_range: entreprise.tranche_effectif_salarie || "",
        creation_date: entreprise.date_creation || "",
        is_active: entreprise.etat_administratif === "A"
      };

      return result;
    } catch (err) {
      const errorMessage = err.message || "Erreur lors de la recherche";
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    lookupSiret,
    isLoading,
    error
  };
}

/**
 * Formater l'adresse depuis les données Sirene
 */
function formatAddress(siege) {
  const parts = [];
  
  if (siege.numero_voie) {
    parts.push(siege.numero_voie);
  }
  if (siege.type_voie) {
    parts.push(siege.type_voie);
  }
  if (siege.libelle_voie) {
    parts.push(siege.libelle_voie);
  }
  
  // Si pas de détails, utiliser l'adresse complète
  if (parts.length === 0 && siege.adresse) {
    return siege.adresse;
  }
  
  return parts.join(" ");
}

export default useSiretLookup;
